import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { CheckinsService } from '@/shared/api/rest/services/checkins.service'
import { adaptCheckinList } from '@/shared/api/rest/checkins.adapters'

export type CheckoutsType = {
    _id: string
    checkedInDate: Date
    checkedOutDate: Date
    child: {
        _id: string
        firstName: string
        middleName: string
        lastName: string
        picture: string
        ageGroup: string
        gender: string
        isCheckedIn: boolean | null
    }
    checkedInParentBy: {
        _id: string
        firstName: string
        lastName: string
        picture: string
        relationship: string
    }
    checkedOutParentBy: {
        _id: string
        firstName: string
        lastName: string
        picture: string
        relationship: string
    }
}

const POLL_INTERVAL = 3000

/**
 * Real-time checkout queue. The legacy GraphQL backend pushed updates over a
 * WebSocket subscription; the REST backend's SSE stream only re-queries every
 * ~3s and can't carry the bearer token from a browser EventSource, so we poll
 * `GET /checkout-queue` on the same cadence — functionally identical.
 */
export function useCheckoutSubscription(branchId: string, ageGroup?: string) {
    const { isLoading, error, data } = useQuery({
        queryKey: ['checkout-queue-sub', branchId, ageGroup ?? ''],
        queryFn: () =>
            CheckinsService.checkoutQueue({ branch: branchId, ageGroup }).then(
                adaptCheckinList
            ) as Promise<CheckoutsType[]>,
        enabled: !!branchId,
        refetchInterval: POLL_INTERVAL
    })

    return {
        loading: isLoading,
        error: error ?? undefined,
        data: data ? { checkoutQueue: data } : undefined
    }
}

export function useGetCheckoutQueue(branchId: string, ageGroup: string) {
    const { isLoading, error, data, refetch } = useQuery({
        queryKey: ['checkout-queue', branchId, ageGroup ?? ''],
        queryFn: () =>
            CheckinsService.checkoutQueue({ branch: branchId, ageGroup }).then(
                adaptCheckinList
            ) as Promise<CheckoutsType[]>,
        enabled: !!branchId,
        refetchInterval: POLL_INTERVAL
    })

    return {
        loading: isLoading,
        error: error ?? undefined,
        data: data ? { getCheckoutQueue: data } : undefined,
        // Polling supersedes the old GraphQL subscription; kept as a no-op so
        // call sites that wired `subscribeToMore` keep working unchanged.
        subscribeToMore: (_options?: unknown) => () => {},
        refetch
    }
}

export function useTakeoutChild() {
    const queryClient = useQueryClient()
    const { mutateAsync, isPending, error } = useMutation({
        mutationFn: (args: { variables: { childId: string } }) =>
            CheckinsService.sendToParent(args.variables.childId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['checkout-queue'] })
            queryClient.invalidateQueries({ queryKey: ['checkout-queue-sub'] })
        }
    })

    return {
        // Mirrors the Apollo mutation call signature used by QueueCard:
        // takeoutChild({ variables: { childId } })
        takeoutChild: mutateAsync,
        loading: isPending,
        error: error ?? undefined
    }
}
